class FreeProduct extends HTMLElement {
  connectedCallback() {
    const cartTotal = parseFloat(this.getAttribute('data-total_price'));
    const freeProductInCart = this.getAttribute('data-freeProduct') === 'true';
    const scriptTag = this.querySelector('script[type="application/json"]');

    if (!scriptTag) return;

    try {
      const productData = JSON.parse(scriptTag.textContent);
      this.cartTotal = cartTotal;
      this.freeProductInCart = freeProductInCart;
      this.productData = productData;
      this.locked = false;

      this.checkAndManageGifts(cartTotal, productData);
    } catch (e) {
      console.error('Error parsing GWP JSON:', e);
    }
  }

  async checkAndManageGifts(cartTotal, productData) {
    if (this.locked) return;
    this.locked = true;

    const cart = await this.fetchCart();

    const giftMap = [
      { threshold: productData.gwp_threshold_1, product: productData.gwp_product_1, key: '_is_gwp_product_1' },
      { threshold: productData.gwp_threshold_2, product: productData.gwp_product_2, key: '_is_gwp_product_2' },
      { threshold: productData.gwp_threshold_3, product: productData.gwp_product_3, key: '_is_gwp_product_3' },
    ];

    const removedGiftVariants = JSON.parse(sessionStorage.getItem('removedGiftVariants') || '[]');

    for (let gift of giftMap) {
      if (!gift.product || !gift.product.id) continue;

      let isInCart = cart.items.find((item) => item.properties?.[gift.key] === 'true');

      // Check replaced variant
      const replacedVariantId = sessionStorage.getItem(`giftReplaced_${gift.key}`);
      if (!isInCart && replacedVariantId) {
        isInCart = cart.items.find((item) => item.variant_id.toString() === replacedVariantId);
      }

      // Remove if below threshold (not manually removed)
      if (cartTotal < gift.threshold) {
        if (isInCart) {
          await this.removeGiftProduct(cart, isInCart.variant_id, gift.key, false); // Not manual
        }
        continue;
      }

      // Remove if manually removed
      if (isInCart && removedGiftVariants.includes(isInCart.variant_id.toString())) {
        await this.removeGiftProduct(cart, isInCart.variant_id, gift.key, true); // Manual
        continue;
      }

      // Skip if manually removed earlier
      if (!isInCart && removedGiftVariants.includes(gift.product.id.toString())) {
        continue;
      }

      // Add gift if not present
      if (!isInCart) {
        await this.addGiftProduct(gift.product.id, gift.key);
      } else if (isInCart.quantity > 1) {
        await this.updateGiftQuantity(isInCart.key, 1);
      }
    }

    this.locked = false;
    this.updateShippingProgress(cartTotal, productData);
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return await response.json();
  }

  async addGiftProduct(variantId, key) {
    const formData = {
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {
            [key]: 'true',
          },
        },
      ],
    };

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      this.triggerCartRefresh();
    } catch (e) {
      console.error(`[FreeProduct] Error adding gift ${variantId}:`, e);
    }
  }

  async updateGiftQuantity(lineItemKey, newQty) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: newQty,
        }),
      });
      this.triggerCartRefresh();
    } catch (e) {
      console.error('[FreeProduct] Error updating gift quantity:', e);
    }
  }

  async removeGiftProduct(cart, variantId, key, manuallyRemoved = false) {
    const item = cart.items.find((item) => item.variant_id === variantId);
    if (!item) return;

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: 0,
        }),
      });

      if (manuallyRemoved) {
        const removed = JSON.parse(sessionStorage.getItem('removedGiftVariants') || '[]');
        if (!removed.includes(variantId.toString())) {
          removed.push(variantId.toString());
          sessionStorage.setItem('removedGiftVariants', JSON.stringify(removed));
        }
      }

      this.triggerCartRefresh();
    } catch (e) {
      console.error(`[FreeProduct] Error removing gift ${variantId}:`, e);
    }
  }

  triggerCartRefresh() {
    if (!location.href.includes('/cart')) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
    } else {
      this.fetchCart().then((cart) => {
        const cartItemsComponent = document.querySelector('cart-items');
        if (!cartItemsComponent) return;

        cart.items.forEach((item, index) => {
          const giftKey = Object.keys(item.properties || {}).find((prop) => prop.startsWith('_is_gwp_product'));
          if (giftKey) {
            cartItemsComponent.updateQuantity(index + 1, item.quantity, `updates[${index}][quantity]`, null);
          }
        });
      });
    }
  }

  updateShippingProgress(cartTotal, productData) {
    const container = document.getElementById('gwp-progress-bar');
    if (!container || !productData) return;

    if (cartTotal <= 0) {
      container.classList.add('hidden');
      return;
    }

    const thresholds = [
      { threshold: productData.gwp_threshold_1, text: productData.gwp_shipping_bar_text_1 },
      { threshold: productData.gwp_threshold_2, text: productData.gwp_shipping_bar_text_2 },
      { threshold: productData.gwp_threshold_3, text: productData.gwp_shipping_bar_text_3 },
    ].filter((t) => t.threshold && t.text);

    if (thresholds.length === 0) {
      container.classList.add('hidden');
      return;
    }

    const maxThreshold = thresholds[thresholds.length - 1].threshold;
    const progressPercent = Math.min((cartTotal / maxThreshold) * 100, 100);

    const fill = container.querySelector('.gwp-bar-fill');
    if (!fill) return;

    fill.style.width = `${progressPercent}%`;
    //fill.style.backgroundColor = `rgb(72, 213, 151)`;
    fill.style.display = 'block';

    container.querySelector('.gwp-bar-label').innerText = `Cart Total: $${(cartTotal / 100).toFixed(2)}`;

    const messages = thresholds.map((t) => {
      if (cartTotal >= t.threshold) {
        return `<span style="font-weight: bold;">You've unlocked: ${t.text}</span>`;
      } else {
        const remaining = ((t.threshold - cartTotal) / 100).toFixed(2);
        return `You’re $${remaining} away from <span>${t.text}</span>`;
      }
    });

    if (thresholds.every((t) => cartTotal >= t.threshold)) {
      messages.push(`<span style="font-weight: bold;">🎉 All gift levels unlocked!</span>`);
    }

    container.querySelector('.gwp-bar-text').innerHTML = messages.join('<br>');
    container.classList.remove('hidden');
  }
}

customElements.define('free-product', FreeProduct);

class cartVariantSelects extends HTMLElement {
  connectedCallback() {
    this.isFreeGift = this.getAttribute('data-free-product') === 'true';
    this.gwpKey = this.getAttribute('data-gwp-key');

    this.getSwatchItems();
  }

  getSwatchItems() {
    this.querySelectorAll('.js-swatch-option').forEach((item) => {
      item.addEventListener('click', () => {
        this.closest('li').querySelector('span.flex').classList.add('hidden');
        this.closest('li').querySelector('.loading__spinner').classList.remove('hidden');
        const newVariantId = item.dataset.variantId;
        const activeItem = this.querySelector('.js-swatch-option.active');
        const currentVariantId = activeItem?.dataset.variantId;

        if (newVariantId === currentVariantId) return;

        this.updateActiveState(item);

        if (this.isFreeGift && this.gwpKey) {
          this.swapFreeGiftVariant(currentVariantId, newVariantId);
        }
      });
    });
  }

  updateActiveState(clickedItem) {
    this.querySelectorAll('.js-swatch-option').forEach((item) => {
      item.classList.remove('active');
      item.setAttribute('aria-pressed', 'false');
    });

    clickedItem.classList.add('active');
    clickedItem.setAttribute('aria-pressed', 'true');
  }

  async swapFreeGiftVariant(oldVariantId, newVariantId) {
    try {
      // Step 1: Remove old gift variant
      if (oldVariantId) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: oldVariantId,
            quantity: 0,
          }),
        });

        const removedGifts = JSON.parse(sessionStorage.getItem('removedGiftProducts') || '[]');
        if (!removedGifts.includes(oldVariantId.toString())) {
          removedGifts.push(oldVariantId.toString());
          sessionStorage.setItem('removedGiftProducts', JSON.stringify(removedGifts));
        }
      }

      // Step 2: Re-allow previously removed gift if it's being reselected
      let removedGifts = JSON.parse(sessionStorage.getItem('removedGiftProducts') || '[]');
      removedGifts = removedGifts.filter((id) => id !== newVariantId.toString());
      sessionStorage.setItem('removedGiftProducts', JSON.stringify(removedGifts));

      // Mark replacement variant to prevent FreeProduct re-adding original
      sessionStorage.setItem(`giftReplaced_${this.gwpKey}`, newVariantId.toString());

      // Step 3: Add new gift
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: newVariantId,
              quantity: 1,
              properties: {
                [this.gwpKey]: 'true',
              },
            },
          ],
        }),
      });

      // Step 4: Refresh cart
      this.triggerCartRefresh();
    } catch (e) {
      console.error('Error swapping gift variant:', e);
    }
  }

  async triggerCartRefresh() {
    if (!location.href.includes('/cart')) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
    } else {
      const cart = await this.fetchCart();
      const cartItemsComponent = document.querySelector('cart-items');
      if (!cartItemsComponent) return;

      cart.items.forEach((item, index) => {
        const giftKey = Object.keys(item.properties || {}).find((prop) => prop.startsWith('_is_gwp_product'));
        if (giftKey) {
          cartItemsComponent.updateQuantity(index + 1, item.quantity, `updates[${index}][quantity]`, null);
        }
      });
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return await response.json();
  }
}

customElements.define('cart-variant-selects', cartVariantSelects);
